import React, { useState, useEffect } from "react";
import { 
  MessageSquare, 
  History, 
  CreditCard, 
  TrendingUp, 
  ChevronRight,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Loader2,
  ExternalLink
} from "lucide-react";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import { useGlobalContext } from "../../context/stateContext";

const SMSBundleUsage = () => {
  const { requestWithAuth } = useAuth();
  const { currentUser } = useGlobalContext();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(0);
  const [history, setHistory] = useState([]);
  const [topUpAmount, setTopUpAmount] = useState("");
  const [calculatedSms, setCalculatedSms] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Predefined options
  const topUpOptions = [
    { amount: 10, units: 180, label: "Starter" },
    { amount: 20, units: 400, label: "Popular" },
    { amount: 50, units: 1250, label: "Business" },
    { amount: 100, units: 2800, label: "Enterprise" },
  ];

  const fetchBundleData = async () => {
    setLoading(true);
    try {
      // Mock API calls - these should be replaced with actual endpoints
      const balanceData = await requestWithAuth("/company/sms-bundle/", "GET");
      const historyData = await requestWithAuth("/company/sms-bundle/purchase/", "GET");
      
      // For now, using mock data
      setBalance(balanceData.bundle_left);
      setHistory(historyData);
    } catch (error) {
      toast.error("Failed to fetch SMS bundle data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBundleData();
    // Load Paystack script
    const script = document.createElement("script");
    script.src = "https://js.paystack.co/v1/inline.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const calculateSmsFromAmount = (amount) => {
    const val = parseFloat(amount);
    if (isNaN(val) || val <= 0) return 0;

    // Logic based on varying rates:
    // < 20 GHS: 18 units/GHS
    // 20 - 49 GHS: 20 units/GHS
    // 50 - 99 GHS: 25 units/GHS
    // 100+ GHS: 28 units/GHS
    
    if (val < 20) return Math.floor(val * 18);
    if (val < 50) return Math.floor(val * 20);
    if (val < 100) return Math.floor(val * 25);
    return Math.floor(val * 28);
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setTopUpAmount(value);
    setCalculatedSms(calculateSmsFromAmount(value));
  };

  const handlePredefinedSelect = (option) => {
    setTopUpAmount(option.amount.toString());
    setCalculatedSms(option.units);
  };

  const handleTopUp = async (e) => {
    e.preventDefault();
    if (!topUpAmount || parseFloat(topUpAmount) <= 0) {
      toast.warning("Please enter a valid amount");
      return;
    }
    const body = 
      {
        "amount_paid": topUpAmount,
        "number_of_sms": calculatedSms
      }
    
    try {
      setSubmitting(true);

      if (!window.PaystackPop) {
        toast.error("Paystack could not be loaded. Please check your internet connection.");
        return;
      }

      const handler = window.PaystackPop.setup({
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
      email: currentUser?.email || "customer@example.com",
      amount: parseFloat(topUpAmount) * 100, 
      currency: "GHS",
      callback: function(response) {
          console.log(response)
          handlePaymentSuccess(response.reference);
        },
        onClose: function() {
          setSubmitting(false);
          toast.info("Payment window closed");
        }
      });

      handler.openIframe();  
      
      
    } catch (error) {
      toast.error("Failed to initiate payment. Please try again.");
    
    }finally{
      setSubmitting(false);
    }
    
  };

  const handlePaymentSuccess = async (reference) => {
    try {
      setProcessingPayment(true)
      await requestWithAuth(`/company/sms-bundle/purchase/`, "POST", { 
        amount_paid: topUpAmount, 
        number_of_sms: calculatedSms,
        reference_code: reference
      });
      
      toast.success(`Payment Successful!`);
      fetchBundleData()
      setTopUpAmount("");
      setCalculatedSms(0);
      
      setProcessingPayment(false)
    } catch (error) {
      toast.error("Failed to verify payment. Please contact support with your reference.");
      setProcessingPayment(false)
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        <p className="text-gray-500 font-medium">Loading SMS bundle details...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 relative">
      {/* Payment Processing Overlay */}
      {processingPayment && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/60 backdrop-blur-md transition-all duration-300">
          <div className="bg-white p-8 rounded-3xl shadow-2xl border border-blue-50 flex flex-col items-center max-w-sm w-full mx-4 transform animate-in fade-in zoom-in duration-300">
            <div className="relative mb-6">
              <div className="absolute inset-0 bg-blue-100 rounded-full animate-ping opacity-25"></div>
              <div className="relative bg-blue-50 p-5 rounded-full">
                <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
            </div>
            
            <h3 className="text-xl font-bold text-gray-900 mb-2 text-center">Processing Payment</h3>
            <p className="text-gray-500 text-center text-sm leading-relaxed mb-6">
              We're verifying your transaction with Paystack. Please do not close this window or refresh the page.
            </p>
            
            <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden relative">
              <div className="progress-bar-fill bg-blue-600 h-full rounded-full"></div>
            </div>

            <style jsx>{`
              .progress-bar-fill {
                width: 30%;
                animation: progress 2s ease-in-out infinite;
              }
              @keyframes progress {
                0% { transform: translateX(-100%); width: 30%; }
                50% { width: 60%; }
                100% { transform: translateX(350%); width: 30%; }
              }
            `}</style>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-gray-500 text-sm mb-1">
            <span>System</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">SMS Bundle</span>
          </div>
          <h1 className="text-3xl font-bold text-gray-900">SMS Bundle Usage</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Balance & Top-up */}
          <div className="lg:col-span-2 space-y-8">
            {/* Balance Card */}
            <div className="bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl p-8 text-white shadow-xl relative overflow-hidden">
              <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                  <div className="p-3 bg-white/20 rounded-xl backdrop-blur-md">
                    <MessageSquare className="w-8 h-8" />
                  </div>
                  <span className="px-4 py-1.5 bg-green-400/20 rounded-full text-sm font-semibold border border-green-400/30">
                    Active
                  </span>
                </div>
                <div>
                  <p className="text-blue-100 text-sm font-medium uppercase tracking-wider mb-1">Current Balance</p>
                  <h2 className="text-5xl font-extrabold mb-2">{balance.toLocaleString()} <span className="text-2xl font-normal opacity-80">Units</span></h2>
                  <div className="flex items-center gap-2 text-blue-100/80 text-sm">
                    <TrendingUp className="w-4 h-4" />
                    <span>Usage: Low (Estimated 45 days remaining)</span>
                  </div>
                </div>
              </div>
              {/* Background Shapes */}
              <div className="absolute top-[-20%] right-[-10%] w-64 h-64 bg-white/10 rounded-full blur-3xl"></div>
              <div className="absolute bottom-[-20%] left-[-10%] w-48 h-48 bg-blue-400/20 rounded-full blur-2xl"></div>
            </div>

            {/* Top-up Form */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center gap-3">
                <div className="p-2 bg-blue-50 rounded-lg text-blue-600">
                  <CreditCard className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Top Up Bundle</h3>
              </div>
              
              <div className="p-8">
                <p className="text-gray-500 text-sm mb-6">Select a bundle package or enter a custom amount below.</p>
                
                {/* Predefined Options */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  {topUpOptions.map((option, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handlePredefinedSelect(option)}
                      className={`p-4 rounded-xl border-2 transition-all text-left flex flex-col justify-between h-32 ${
                        topUpAmount === option.amount.toString()
                          ? "border-blue-500 bg-blue-50/50 ring-2 ring-blue-500/10"
                          : "border-gray-100 hover:border-blue-200 hover:bg-gray-50"
                      }`}
                    >
                      <div>
                        <span className={`text-xs font-bold uppercase tracking-wider mb-1 block ${
                          topUpAmount === option.amount.toString() ? "text-blue-600" : "text-gray-400"
                        }`}>
                          {option.label}
                        </span>
                        <div className="text-xl font-bold text-gray-900">{option.amount} <span className="text-sm font-medium text-gray-500">GHS</span></div>
                      </div>
                      <div className="text-sm text-gray-600 font-medium">
                        {option.units.toLocaleString()} <span className="text-xs opacity-70">SMS</span>
                      </div>
                    </button>
                  ))}
                </div>

                {/* Custom Amount Form */}
                <form onSubmit={handleTopUp} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Custom Amount (GHS)</label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-blue-500 transition-colors font-bold">
                        ₵
                      </div>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={topUpAmount}
                        onChange={handleAmountChange}
                        className="block w-full pl-10 pr-4 py-4 text-gray-900 border-2 border-gray-100 rounded-xl focus:border-blue-500 focus:outline-none focus:ring-4 focus:ring-blue-500/5 transition-all text-lg font-medium"
                      />
                    </div>
                  </div>

                  {/* Calculation Preview */}
                  {topUpAmount && parseFloat(topUpAmount) > 0 && (
                    <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-500 text-white rounded-full p-1">
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                        <span className="text-blue-900 font-medium text-sm">Estimated SMS Units:</span>
                      </div>
                      <span className="text-blue-700 font-bold text-xl leading-none">
                        {calculatedSms.toLocaleString()}
                      </span>
                    </div>
                  )}

                  <button
                    disabled={submitting || !topUpAmount || parseFloat(topUpAmount) <= 0}
                    className={`w-full py-4 rounded-xl font-bold text-white shadow-lg transition-all flex items-center justify-center gap-2 ${
                      submitting || !topUpAmount || parseFloat(topUpAmount) <= 0
                        ? "bg-gray-300 cursor-not-allowed shadow-none"
                        : "bg-blue-600 hover:bg-blue-700 hover:shadow-blue-500/20 active:scale-[0.98]"
                    }`}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Proceed to Payment</span>
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* Right Column: Information & Activity */}
          <div className="space-y-8">
           

            {/* Recent History */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History className="w-5 h-5 text-gray-500" />
                  <h3 className="font-bold text-gray-900 text-sm">Purchase History</h3>
                </div>
               
              </div>
              <div className="divide-y divide-gray-50">
                {history.map((item) => (
                  <div key={item.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-bold text-gray-900">₵{item.amount_paid}</span>
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-md text-[10px] font-bold uppercase tracking-wider">
                        {item.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{item.number_of_sms.toLocaleString()} Units</span>
                      <span>{item.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSBundleUsage;
